import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AppError } from '../../api/errors.js';
import { useDataHubClient } from '../../context/DataHubClientContext.js';
import type { TestSet } from '../../services/testSetService.interface.js';
import { TestSetService } from '../../services/testSetService.js';

export interface UseTestSetResult {
  testSet: TestSet | null;
  isLoading: boolean;
  error: string | null;
  errorStatusCode: number | undefined;
  refetch: () => void;
}

export function useTestSet(testSetId: string): UseTestSetResult {
  const client = useDataHubClient();
  const service = useMemo(() => new TestSetService(client), [client]);

  const [testSet, setTestSet] = useState<TestSet | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [errorStatusCode, setErrorStatusCode] = useState<number | undefined>(
    undefined,
  );
  const [fetchTick, setFetchTick] = useState(0);
  const prevTestSetIdRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const isSameTestSet = prevTestSetIdRef.current === testSetId;
    prevTestSetIdRef.current = testSetId;

    if (!isSameTestSet) {
      setIsLoading(true);
    }
    setError(null);
    setErrorStatusCode(undefined);

    service
      .getTestSet(testSetId)
      .then((result) => {
        if (cancelled) return;
        setTestSet(result);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(
          err instanceof Error ? err.message : 'Failed to load test set',
        );
        setErrorStatusCode(
          err instanceof AppError ? err.statusCode : undefined,
        );
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [service, testSetId, fetchTick]);

  const refetch = useCallback(() => setFetchTick((t) => t + 1), []);

  return { testSet, isLoading, error, errorStatusCode, refetch };
}
